import { Injectable } from '@angular/core';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { environment } from '@quorum/environments';
import { Vehicle } from '@quorum/models/xs-resource';
import { filter, take, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GmTireCentralService {
  private iframe!: HTMLIFrameElement;
  private widgetContainerSelector = '.widget-container';

  constructor(private authenticationStateService: AuthenticationStateService) {}

  async setupGMTireCentralIframe(vehicle: Vehicle, workOrderId: number, dealerCode: string, dealerCountryCode: string) {
    this.iframe = document.createElement('iframe');
    this.iframe.width = '100%';
    this.iframe.height = '700';
    this.iframe.style.border = 'none';
    this.iframe.style.borderRadius = '8px';
    this.iframe.className = 'shadow-lg';

    const container = await this.getWidgetContainer();
    container.appendChild(this.iframe);

    this.writeIframeContents(vehicle, workOrderId, dealerCode, dealerCountryCode);

    this.observeWidgetReadyAndInjectStyles(this.iframe);
  }

  private observeWidgetReadyAndInjectStyles(iframe: HTMLIFrameElement): void {
    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDocument) {
      console.error('Unable to access iframe document for MutationObserver.');
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      const widget = iframeDocument.querySelector('ami-tirefinder');

      if (widget) {
        // Widget has been added to DOM, inject styles
        this.injectCustomStyles(iframe);
        obs.disconnect(); // Stop observing
      }
    });

    observer.observe(iframeDocument.body, {
      childList: true,
      subtree: true,
    });
  }

  private injectCustomStyles(iframe: HTMLIFrameElement): void {
    const iframeWindow = iframe.contentWindow;
    const iframeDocument = iframe.contentDocument || iframeWindow?.document;

    if (!iframeDocument) {
      console.error('Unable to access iframe document for styling.');
      return;
    }

    // Add a small delay to ensure the widget is fully rendered
    setTimeout(() => {
      const style = document.createElement('style');
      style.innerHTML = `
          .ami-tirefinder .amibs-btn-default,
          .ami-tirefinder .btn-default {
            background: #0054A6 !important;
            border-radius: 6px !important;
            height: 28px !important;
            font-size: 14px !important;
            align-items: center !important;
            display: flex !important;
            padding: 6px 12px !important;
          }

          .ami-tirefinder .link,
          .ami-tirefinder .close-icon,
          .title-tile,
          .dist-qty,
          .enabled,
          .tire-name,
          .tire-name-subtitle,
          .gm-app .results-tire .title,
          .gm-app .results-tire .subtitle,
          .gm-app .results-tire .price-des .subtitle,
          .load-speed-menu .fa,
          .sidebar-filter-search .fa-toggle,
          a {
            color: #0054A6 !important;
          }

          .ami-tirefinder .close-icon {
            background: #0054A6 !important;
            color: #ffffff !important;
          }

        `;

      // Add the style element to the iframe's document head
      iframeDocument.head.appendChild(style);
    }, 1000);
  }

  private async getWidgetContainer(): Promise<Element | null> {
    let container = document.querySelector(this.widgetContainerSelector);
    if (!container) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      container = document.querySelector(this.widgetContainerSelector);
    }
    return container;
  }

  private writeIframeContents(vehicle: Vehicle, workOrderId: number, dealerCode: string, dealerCountryCode: string) {
    if (!this.iframe) {
      console.error('iframe not initialized.');
      return;
    }

    const iframeDocument = this.iframe.contentDocument || this.iframe.contentWindow?.document;
    if (!iframeDocument) {
      console.error('Unable to access iframe document.');
      return;
    }

    iframeDocument.open();
    iframeDocument.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <title>Tire Finder</title>
          </head>
          <body style="margin:10px;padding:10px;">
            <ami-tirefinder></ami-tirefinder>
          </body>
        </html>
      `);
    iframeDocument.close();

    this.injectIframeScripts(vehicle, workOrderId, dealerCode, dealerCountryCode);
  }

  private async injectIframeScripts(
    vehicle: Vehicle,
    workOrderId: number,
    dealerCode: string,
    dealerCountryCode: string
  ) {
    const vin = vehicle.vin;
    const timestamp = new Date().toISOString().split('.')[0] + 'Z';
    const hostName = window.location.origin;
    const toolProvider = 'Quorum';

    const destinationId = await this.authenticationStateService
      .selectAuthenticatedUser()
      .pipe(
        filter((authenticatedUser) => authenticatedUser != null),
        take(1),
        map((authenticatedUser) => authenticatedUser.user.destination)
      )
      .toPromise();

    const plainText = `${dealerCode}_${hostName}_${toolProvider}_${environment.environment}_${timestamp}`;
    const signature = await this.generateSignatureForGMTireCentralWidget(plainText, environment.gmTireCentral);
    const quoteIdentifier = `${workOrderId}_${destinationId}`;

    const dataInit = {
      language: 'en',
      country: dealerCountryCode,
      dealerCode,
      environment: environment.environment,
      toolProvider,
      timeStamp: timestamp,
      signature,
      userName: 'test',
      vin,
      quoteIndentifierId: quoteIdentifier,
    };

    const iframeWindow = this.iframe.contentWindow;
    const iframeDoc = this.iframe.contentDocument || iframeWindow?.document;

    if (!iframeWindow || !iframeDoc) {
      console.error('Unable to inject scripts.');
      return;
    }

    (iframeWindow as any).parentInit = dataInit;

    (iframeWindow as any).initializeWidget = (pageLoad = false) => {
      const init = (iframeWindow as any).parentInit;
      const rootElement = iframeDoc.getElementsByTagName('ami-tirefinder')[0] as HTMLElement;
      if (rootElement) {
        rootElement.dataset.init = JSON.stringify(init);
        if (!pageLoad && typeof (iframeWindow as any).reloadTireWidget === 'function') {
          (iframeWindow as any).reloadTireWidget();
        }
      }
    };

    (iframeWindow as any).tirefinderQuoteStatus = (status: any) => {
      if (window.parent && typeof (window.parent as any).tirefinderQuoteStatus === 'function') {
        (window.parent as any).tirefinderQuoteStatus(status);
      }
    };

    const widgetScript = iframeDoc.createElement('script');
    widgetScript.src =
      environment.environment === 'staging'
        ? 'https://wwwstg.gmtirecentral.com/widget.js'
        : 'https://www.gmtirecentral.com/widget.js';
    widgetScript.id = 'gm-tire-widget';
    widgetScript.type = 'text/javascript';

    widgetScript.onload = () => {
      (iframeWindow as any).initializeWidget(true);
    };

    iframeDoc.body.appendChild(widgetScript);
  }

  generateSignatureForGMTireCentralWidget(plaintext: string, secretKey: string) {
    const encoder = new TextEncoder();
    const plaintextData = encoder.encode(plaintext);
    const keyData = encoder.encode(secretKey);

    return window.crypto.subtle
      .importKey('raw', keyData, { name: 'HMAC', hash: { name: 'SHA-256' } }, false, ['sign'])
      .then((key) => window.crypto.subtle.sign('HMAC', key, plaintextData))
      .then((signature) => btoa(String.fromCharCode(...new Uint8Array(signature))));
  }
}
